import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LogInIcon, MailIcon, ShieldCheckIcon, ArrowLeftIcon } from 'lucide-react';
import { LoadingScreen } from '../../../shared/ui/LoadingScreen';
import { useAuth } from '../../../app/providers/AuthProvider';
import { theme, getTenant } from '../../../theme/tokens';
const LOGIN_IMAGES = [
// Petronas Twin Towers, Kuala Lumpur
'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=1200&q=80&fit=crop',
// Kuala Lumpur skyline at dusk
'https://images.unsplash.com/photo-1508062878650-88b52897f298?w=1200&q=80&fit=crop',
// Batu Caves, Selangor
'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?w=1200&q=80&fit=crop&sat=-10'];
// Destructure for cleaner access
const {
  brand,
  colors
} = theme;
export function Login() {
  const {
    t
  } = useTranslation('auth');
  const navigate = useNavigate();
  const {
    login
  } = useAuth();
  const [step, setStep] = useState<'login' | 'mfa' | 'loading'>('login');
  const [email, setEmail] = useState('simon.verraest@bilabila.co');
  const [mfaCode, setMfaCode] = useState(['', '', '', '', '', '']);
  const [resendCountdown, setResendCountdown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [heroImage] = useState(() => LOGIN_IMAGES[Math.floor(Math.random() * LOGIN_IMAGES.length)]);
  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);
  // Auto-focus first MFA input when entering MFA step
  useEffect(() => {
    if (step === 'mfa') {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [step]);
  const getMaskedEmail = () => {
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const masked = local.charAt(0) + '•••' + (local.length > 1 ? local.charAt(local.length - 1) : '');
    return `${masked}@${domain}`;
  };
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('mfa');
    setResendCountdown(30);
  };
  const handleMfaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('loading');
    setTimeout(() => {
      login();
      navigate('/overview');
    }, 2000);
  };
  const handleMfaInput = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...mfaCode];
      digits.forEach((digit, i) => {
        if (index + i < 6) newCode[index + i] = digit;
      });
      setMfaCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }
    if (!/^\d*$/.test(value)) return;
    const newCode = [...mfaCode];
    newCode[index] = value;
    setMfaCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleMfaKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !mfaCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const newCode = [...mfaCode];
      newCode[index - 1] = '';
      setMfaCode(newCode);
    }
  };
  const handleResend = () => {
    if (resendCountdown > 0) return;
    setMfaCode(['', '', '', '', '', '']);
    setResendCountdown(30);
    inputRefs.current[0]?.focus();
  };
  const isCodeComplete = mfaCode.every(digit => digit !== '');
  if (step === 'loading') {
    return <LoadingScreen data-id="element-2645" />;
  }
  return <div className="min-h-screen flex bg-white" data-id="element-2646">
      {/* Left Side - Image & Branding (Hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative" style={{
      backgroundColor: colors.background.hero
    }} data-id="element-2647">
        <img src={heroImage} alt={t('pages.Login.attrs.singapore')} className="absolute inset-0 w-full h-full object-cover opacity-60" data-id="element-2648" />

        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent" data-id="element-2649" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full" data-id="element-2650">
          <div data-id="element-2651">
            <img src={getTenant().brand.logo?.light} alt={getTenant().brand.name} className="h-12 w-auto object-contain" data-id="element-2652" />
          </div>
          <div className="max-w-xl" data-id="element-2653">
            <h1 className="text-4xl font-bold mb-4 leading-tight" style={{
            color: colors.text.inverse
          }} data-id="element-2654">
              {t('pages.Login.powering_singapores_digital_commerce')}
            </h1>
            <p className="text-lg text-gray-200" data-id="element-2655">
              {t('pages.Login.the_complete_payment_solution_for_modern')}
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form / MFA */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center px-6 py-12 sm:px-12" data-id="element-2656">
        <div className="w-full" style={{
        maxWidth: theme.layout.auth.maxWidth
      }} data-id="element-2657">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8" data-id="element-2658">
            <img src={getTenant().brand.logo?.light} alt={getTenant().brand.name} className="h-10 w-auto object-contain" data-id="element-2659" />
          </div>

          {step === 'login' && <>
              <div className="mb-10 text-center lg:text-left" data-id="element-2660">
                <h2 className="text-3xl font-bold mb-2" style={{
              color: colors.text.primary
            }} data-id="element-2661">
                  {t('pages.Login.merchant_portal')}
                </h2>
                <p style={{
              color: colors.text.secondary
            }} data-id="element-2662">
                  {t('pages.Login.sign_in_to_manage_your_business')}
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-6" data-id="element-2663">
                <div data-id="element-2664">
                  <label className="block text-sm font-medium mb-2" style={{
                color: colors.neutral[700]
              }} data-id="element-2665">
                    {t('pages.Login.email_address')}
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('pages.Login.attrs.simonverraestsiamtradingth')} className="w-full px-4 py-3 rounded-lg outline-none transition-colors" style={{
                border: `1px solid ${colors.border.DEFAULT}`,
                backgroundColor: colors.neutral[50]
              }} onFocus={e => {
                e.target.style.borderColor = brand.primary;
                e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                e.target.style.backgroundColor = colors.neutral[0];
              }} onBlur={e => {
                e.target.style.borderColor = colors.border.DEFAULT;
                e.target.style.boxShadow = 'none';
                e.target.style.backgroundColor = colors.neutral[50];
              }} required data-id="element-2666" />
                </div>

                <div data-id="element-2667">
                  <label className="block text-sm font-medium mb-2" style={{
                color: colors.neutral[700]
              }} data-id="element-2668">
                    {t('pages.Login.password')}
                  </label>
                  <input type="password" defaultValue="••••••••" placeholder="••••••••" className="w-full px-4 py-3 rounded-lg outline-none transition-colors" style={{
                border: `1px solid ${colors.border.DEFAULT}`,
                backgroundColor: colors.neutral[50]
              }} onFocus={e => {
                e.target.style.borderColor = brand.primary;
                e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                e.target.style.backgroundColor = colors.neutral[0];
              }} onBlur={e => {
                e.target.style.borderColor = colors.border.DEFAULT;
                e.target.style.boxShadow = 'none';
                e.target.style.backgroundColor = colors.neutral[50];
              }} required data-id="element-2669" />
                </div>

                <div className="flex items-center justify-between" data-id="element-2670">
                  <label className="flex items-center cursor-pointer" data-id="element-2671">
                    <input type="checkbox" className="h-4 w-4 border-gray-300 rounded cursor-pointer" style={{
                  accentColor: brand.primary
                }} data-id="element-2672" />

                    <span className="ml-2 text-sm" style={{
                  color: colors.text.secondary
                }} data-id="element-2673">
                      {t('pages.Login.remember_me')}
                    </span>
                  </label>
                  <button type="button" className="text-sm font-medium transition-colors" style={{
                color: colors.text.link
              }} onMouseEnter={e => e.currentTarget.style.color = colors.text.linkHover} onMouseLeave={e => e.currentTarget.style.color = colors.text.link} data-id="element-2674">
                    {t('pages.Login.forgot_password')}
                  </button>
                </div>

                <button type="submit" className="w-full font-medium py-3.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm" style={{
              backgroundColor: brand.primary,
              color: colors.neutral[900]
            }} onMouseEnter={e => e.currentTarget.style.backgroundColor = brand.primaryHover} onMouseLeave={e => e.currentTarget.style.backgroundColor = brand.primary} data-id="element-2675">
                  <LogInIcon size={18} className="mr-2" data-id="element-2676" />
                  {t('pages.Login.sign_in')}
                </button>
              </form>

              <div className="mt-8 text-center lg:text-left" data-id="element-2677">
                <p className="text-sm" style={{
              color: colors.text.secondary
            }} data-id="element-2678">
                  {t('pages.Login.dont_have_a_merchant_account')}{' '}
                  <button className="font-medium transition-colors" style={{
                color: colors.text.link
              }} onMouseEnter={e => e.currentTarget.style.color = colors.text.linkHover} onMouseLeave={e => e.currentTarget.style.color = colors.text.link} data-id="element-2679">
                    {t('pages.Login.contact_sales')}
                  </button>
                </p>
              </div>
            </>}

          {step === 'mfa' && <>
              <button type="button" onClick={() => {
            setStep('login');
            setMfaCode(['', '', '', '', '', '']);
          }} className="flex items-center text-sm mb-8 transition-colors" style={{
            color: colors.text.tertiary
          }} onMouseEnter={e => e.currentTarget.style.color = colors.neutral[700]} onMouseLeave={e => e.currentTarget.style.color = colors.text.tertiary} data-id="element-2680">
                <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-2681" />
                {t('pages.Login.back_to_sign_in')}
              </button>

              <div className="text-center mb-8" data-id="element-2682">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{
              backgroundColor: brand.primaryLight
            }} data-id="element-2683">
                  <MailIcon size={28} style={{
                color: brand.primary
              }} data-id="element-2684" />
                </div>
                <h2 className="text-2xl font-bold mb-2" style={{
              color: colors.text.primary
            }} data-id="element-2685">
                  {t('pages.Login.check_your_email')}
                </h2>
                <p className="text-sm leading-relaxed" style={{
              color: colors.text.secondary
            }} data-id="element-2686">
                  {t('pages.Login.weve_sent_a_6digit_verification_code')}
                </p>
                <p className="font-medium text-sm mt-1" style={{
              color: colors.text.primary
            }} data-id="element-2687">
                  {getMaskedEmail()}
                </p>
              </div>

              <form onSubmit={handleMfaSubmit} className="space-y-6" data-id="element-2688">
                <div data-id="element-2689">
                  <label className="block text-sm font-medium mb-3 text-center" style={{
                color: colors.neutral[700]
              }} data-id="element-2690">
                    {t('pages.Login.enter_verification_code')}
                  </label>
                  <div className="flex justify-center gap-3" data-id="element-2691">
                    {mfaCode.map((digit, index) => <input key={index} ref={el => {
                  inputRefs.current[index] = el;
                }} type="text" inputMode="numeric" maxLength={6} value={digit} onChange={e => handleMfaInput(index, e.target.value)} onKeyDown={e => handleMfaKeyDown(index, e)} className="w-12 h-14 text-center text-xl font-semibold rounded-lg outline-none transition-all" style={{
                  border: `1px solid ${digit ? brand.primary : colors.border.DEFAULT}`,
                  backgroundColor: digit ? brand.primarySubtle : colors.neutral[50],
                  boxShadow: digit ? `0 0 0 1px ${brand.primaryRingActive}` : 'none'
                }} onFocus={e => {
                  e.target.select();
                  if (!digit) {
                    e.target.style.borderColor = brand.primary;
                    e.target.style.boxShadow = `0 0 0 2px ${brand.primaryRing}`;
                    e.target.style.backgroundColor = colors.neutral[0];
                  }
                }} onBlur={e => {
                  if (!digit) {
                    e.target.style.borderColor = colors.border.DEFAULT;
                    e.target.style.boxShadow = 'none';
                    e.target.style.backgroundColor = colors.neutral[50];
                  }
                }} data-id="element-2692" />)}
                  </div>
                </div>

                <button type="submit" disabled={!isCodeComplete} className="w-full font-medium py-3.5 px-4 rounded-lg transition-colors flex items-center justify-center shadow-sm" style={{
              backgroundColor: isCodeComplete ? brand.primary : colors.neutral[100],
              color: isCodeComplete ? colors.neutral[900] : colors.neutral[400],
              cursor: isCodeComplete ? 'pointer' : 'not-allowed'
            }} onMouseEnter={e => {
              if (isCodeComplete) e.currentTarget.style.backgroundColor = brand.primaryHover;
            }} onMouseLeave={e => {
              if (isCodeComplete) e.currentTarget.style.backgroundColor = brand.primary;
            }} data-id="element-2693">
                  <ShieldCheckIcon size={18} className="mr-2" data-id="element-2694" />
                  {t('pages.Login.verify_sign_in')}
                </button>
              </form>

              <div className="mt-6 text-center" data-id="element-2695">
                <p className="text-sm" style={{
              color: colors.text.tertiary
            }} data-id="element-2696">
                  {t('pages.Login.didnt_receive_the_code')}{' '}
                  {resendCountdown > 0 ? <span style={{
                color: colors.neutral[400]
              }} data-id="element-2697">
                      {t('pages.Login.resend_in')} {resendCountdown}
                      {t('pages.Login.s')}
                    </span> : <button type="button" onClick={handleResend} className="font-medium transition-colors" style={{
                color: colors.text.link
              }} onMouseEnter={e => e.currentTarget.style.color = colors.text.linkHover} onMouseLeave={e => e.currentTarget.style.color = colors.text.link} data-id="element-2698">
                      {t('pages.Login.resend_code')}
                    </button>}
                </p>
              </div>

              <div className="mt-4 p-3.5 rounded-lg" style={{
            backgroundColor: colors.neutral[50],
            border: `1px solid ${colors.border.light}`
          }} data-id="element-2699">
                <div className="flex items-start gap-2.5" data-id="element-2700">
                  <ShieldCheckIcon size={16} className="mt-0.5 flex-shrink-0" style={{
                color: colors.neutral[400]
              }} data-id="element-2701" />

                  <p className="text-xs leading-relaxed" style={{
                color: colors.text.tertiary
              }} data-id="element-2702">
                    {t('pages.Login.for_your_security_this_code_expires')}
                  </p>
                </div>
              </div>
            </>}

          <div className="mt-16 pt-6 text-center lg:text-left" style={{
          borderTop: `1px solid ${colors.border.light}`
        }} data-id="element-2703">
            <p className="text-xs" style={{
            color: colors.text.tertiary
          }} data-id="element-2704">
              © 2026 {getTenant().brand.name}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>;
}